import React, { useState, useEffect } from 'react';
import { authService, accountService } from '../services/services';

const Settings = () => {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
  const [formData, setFormData] = useState({
    name: '',
    balance: '',
  });

  useEffect(() => {
    loadUser();
    loadAccounts();
  }, []);

  const loadUser = async () => {
    try {
      const data = await authService.getMe();
      setUser(data);
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await accountService.list();
      setAccounts(data);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await accountService.update(editingAccount.id, formData);
      } else {
        await accountService.create(formData);
      }
      setShowAccountModal(false);
      setEditingAccount(null);
      setFormData({ name: '', balance: '' });
      loadAccounts();
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao salvar conta');
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      balance: account.balance,
    });
    setShowAccountModal(true);
  };

  const handleDeleteAccount = async (id) => {
    setDeleteConfirm({ show: true, id });
  };

  const confirmDelete = async () => {
    if (deleteConfirm.id) {
      try {
        await accountService.delete(deleteConfirm.id);
        loadAccounts();
        setDeleteConfirm({ show: false, id: null });
      } catch (error) {
        alert(error.response?.data?.error || 'Erro ao excluir conta');
        setDeleteConfirm({ show: false, id: null });
      }
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Configurações</h1>
        <p className="text-sm text-gray-400 mt-1.5">Gerencie suas preferências e contas</p>
      </div>

      {/* Informações do Usuário */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-white">Informações do Usuário</h2>
        </div>
        {user && (
          <div className="space-y-2">
            <div>
              <span className="text-sm text-gray-600">Nome: </span>
              <span className="text-sm font-medium text-gray-900">{user.name}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Email: </span>
              <span className="text-sm font-medium text-gray-900">{user.email}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Cadastrado em: </span>
              <span className="text-sm font-medium text-gray-900">
                {new Date(user.created_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Contas */}
      <div className="card">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-white">Contas</h2>
          </div>
          <button
            onClick={() => {
              setEditingAccount(null);
              setFormData({ name: '', balance: '' });
              setShowAccountModal(true);
            }}
            className="btn-primary"
          >
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Conta
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {accounts.length > 0 ? (
              accounts.map((account) => (
                <div key={account.id} className="p-6 flex justify-between items-center transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-200">{account.name}</p>
                      <p className="text-sm text-gray-400 mt-0.5">
                        Saldo: <span className="font-medium">{formatCurrency(account.balance)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditAccount(account)}
                      className="btn-ghost text-xs p-1.5"
                      title="Editar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteAccount(account.id)}
                      className="btn-ghost text-xs p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Excluir"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <p className="text-sm text-gray-500">Nenhuma conta cadastrada</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Conta */}
      {showAccountModal && (
        <div className="modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="text-lg font-bold text-white">
                  {editingAccount ? 'Editar Conta' : 'Nova Conta'}
                </h3>
                <p className="text-xs text-gray-300 mt-1">Defina os dados da conta</p>
              </div>
            </div>
            <form onSubmit={handleAccountSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="modal-body">
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Nome</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-modern"
                      placeholder="Nome da conta"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1.5">Saldo Inicial</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.balance}
                      onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                      className="input-modern"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccountModal(false);
                      setEditingAccount(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {deleteConfirm.show && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm({ show: false, id: null })}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center border border-red-800/50">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Confirmar Exclusão</h3>
                  <p className="text-xs text-gray-300 mt-1">Esta ação não pode ser desfeita</p>
                </div>
              </div>
            </div>
            <div className="modal-body">
              <p className="text-sm text-gray-300">
                Tem certeza que deseja excluir esta conta?
              </p>
            </div>
            <div className="modal-footer">
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm({ show: false, id: null })}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg shadow-lg hover:bg-red-700 hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;

